/**
 * 新增/编辑用户弹窗 — 从 AdminPanel 中提取
 */
import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { UserInfo, UserRole } from '../types';

interface UserFormDialogProps {
  editingUser: UserInfo | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (form: UserFormData) => Promise<void>;
}

export interface UserFormData {
  username: string;
  password: string;
  display_name: string;
  role: UserRole;
}

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  editingUser,
  isOpen,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<UserFormData>({
    username: '',
    password: '',
    display_name: '',
    role: 'normal',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // 重置表单（当 editingUser 变化时）
  React.useEffect(() => {
    if (isOpen) {
      setForm({
        username: editingUser?.username || '',
        password: '',
        display_name: editingUser?.display_name || '',
        role: editingUser?.role || 'normal',
      });
      setError(null);
      setShowPassword(false);
    }
  }, [isOpen, editingUser]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!form.username.trim()) {
      setError('用户名不能为空');
      return;
    }
    if (!editingUser && !form.password.trim()) {
      setError('密码不能为空');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err: any) {
      setError(err?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center z-30">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {editingUser ? '编辑用户' : '新增用户'}
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              disabled={!!editingUser}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border disabled:bg-gray-100"
              placeholder="用户名"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              密码 {editingUser && <span className="text-gray-400 font-normal">（留空不修改）</span>}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border pr-10"
                placeholder={editingUser ? '留空不修改密码' : '密码（至少6位）'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                title={showPassword ? '隐藏密码' : '显示密码'}
              >
                {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">显示名称</label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
              placeholder="显示名称（可选）"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
            >
              <option value="admin">管理员</option>
              <option value="enterprise">企业用户</option>
              <option value="agent">代理用户</option>
              <option value="normal">普通用户</option>
            </select>
          </div>
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {saving ? '保存中...' : '确认保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserFormDialog;
